import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useForm from '@components/form/hooks/useForm';
import usePrevious from 'lib/src/hooks/usePrevious';

import { createAdminUser } from '@actions/adminUsers/postCreateAdminUser';
import {
    getAdminUserIsPosting,
    getAdminUserPostSuccess,
    getAdminUsersError,
} from '@selectors/adminUsers';
import { useHistory } from 'react-router-dom';
import { CloseModal, HandleSubmit } from 'src/types/shared/Functions';
import useSite from 'lib/src/hooks/useSite';
import { site as siteEnum } from 'lib/src/shared/enums/site';
import { DropdownOption } from 'lib/src/types/shared/DropdownOption';
import { userRoles } from 'lib/src/shared/enums/dropdownEnums';

export default function useCreateAdmin(): UseCreateAdminReturn {
    const dispatch = useDispatch();
    const history = useHistory();

    const isPosting = useSelector(getAdminUserIsPosting);
    const postSuccess = useSelector(getAdminUserPostSuccess);
    const error = useSelector(getAdminUsersError);
    const site = useSite();
    const initialFormData: FormState = {
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        roles: [],
        site: site ?? siteEnum.IX,
    };
    const prevProps = usePrevious({ postSuccess, site });

    const [formState, handleChange] = useForm(initialFormData);

    useEffect(() => {
        if (!!site && site !== prevProps.site) {
            handleChange('site', site);
        }
    }, [site, handleChange, prevProps.site]);

    const handleSubmit = useCallback(() => {
        dispatch(createAdminUser(formState));
    }, [dispatch, formState]);

    const closeModal = useCallback(() => {
        history.push('/admins');
    }, [history]);

    useEffect(() => {
        if (!prevProps.postSuccess && postSuccess) {
            closeModal();
        }
    }, [prevProps.postSuccess, postSuccess, closeModal]);

    const siteOptions: DropdownOption<siteEnum>[] = [
        { label: 'All', value: siteEnum.ALL },
        { label: 'IX', value: siteEnum.IX },
        { label: 'GA', value: siteEnum.GA },
    ];
    const shouldShowSiteOptions = site === siteEnum.ALL;

    return {
        isPosting,
        error,
        formState,
        handleChange,
        handleSubmit,
        closeModal,
        siteOptions,
        shouldShowSiteOptions,
    };
}

interface FormState {
    email: string;
    password: string;
    confirmPassword: string;
    firstName: string;
    lastName: string;
    roles: userRoles[];
    site: siteEnum;
}

interface UseCreateAdminReturn {
    isPosting: boolean;
    error: string | null;
    formState: FormState;
    handleChange: <T>(name: keyof FormState, value: T) => void;
    handleSubmit: HandleSubmit;
    closeModal: CloseModal;
    siteOptions: DropdownOption<siteEnum>[];
    shouldShowSiteOptions: boolean;
}
